import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tag, Typography, Table, Input, Space, Dropdown, Button, App, Tooltip } from 'antd';
import { MoreOutlined, EditOutlined, PlusOutlined, DeleteOutlined, LockOutlined } from '@ant-design/icons';
import { deletePlan, listPlans, updatePlansStatus, } from '../../services/plans';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;
const { useApp } = App;

const PlansList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { can } = usePermissions();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (page, pageSize, srt, q) => {
    setLoading(true);
    try {
      const { items, total } = await listPlans({
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        search: q || undefined,
        sortField: srt?.field,
        sortOrder: srt?.order,
      });
      setData(items || []);
      setPagination(prev => ({
        ...prev,
        current: page || prev.current,
        pageSize: pageSize || prev.pageSize,
        total,
      }));
    } catch (err) {
      message.error('No se pudo cargar la lista de regiones.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    // al buscar, reinicia a página 1
    fetchData(1, pagination.pageSize, sorter, value || '');
  };

  // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    switch (action) {
      case 'edit':
        navigate(`/memberships/${record.id}/edit`);
        break;
      case 'toggle':
        const willActivate = !record.isActive;
        modal.confirm({
          title: willActivate ? '¿Activar membresía?' : '¿Desactivar membresía?',
          content: `${record.description || 'membresía'}`,
          okText: willActivate ? 'Activar' : 'Desactivar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await updatePlansStatus(record.id, willActivate);
              message.success(willActivate ? 'Membresía activada' : 'Membresía desactivada');
              fetchData(pagination.current, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo actualizar el estatus');
            }
          },
        });
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar Membresía?',
          content: `${record.description || 'Membresía'}`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await deletePlan(record.id);
              message.success('Membresía eliminada');
              const nextPage = (data.length === 1 && pagination.current > 1)
                ? pagination.current - 1
                : pagination.current;
              setPagination(prev => ({ ...prev, current: nextPage }));
              fetchData(nextPage, pagination.pageSize, sorter, search);
            } catch (error) {
              message.error('No se pudo eliminar la membresa');
            }
          },
        });
        break;

      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const menuItems = [
      can('memberships.edit') && {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Editar Membresía',
      },
      can('memberships.toggle') && {
        key: 'toggle',
        icon: <LockOutlined />,
        label: record.isActive ? 'Desactivar' : 'Activar',
      },
      {
        type: 'divider',
      },
      can('memberships.delete') && {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Borrar Membresía',
        danger: true,
      },
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    { title: 'Código', dataIndex: 'code', key: 'code', sorter: true, },
    { title: 'Membresía', dataIndex: 'name', key: 'name', sorter: true, },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
      sorter: true,
      render: (text) => (
        <div style={{
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          maxWidth: 250,
          lineHeight: '1.4'
        }}>
          {text}
        </div>
      )
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      align: 'center',
      render: (color) => (
        <Tooltip title={color || 'Sin color'}>
          <div style={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: color || 'transparent',
            border: '1px solid #d9d9d9',
            margin: '0 auto'
          }} />
        </Tooltip>
      )
    },
    {
      title: 'Precio', dataIndex: 'price', key: 'price', sorter: true,
      render: (value, record) =>
        new Intl.NumberFormat('es-MX', {
          style: 'currency',
          currency: record.currency || 'MXN',
        }).format(value),
    },
    { title: 'Moneda', dataIndex: 'currency', key: 'currency', sorter: true, },
    { title: 'Vigencia (días)', dataIndex: 'durationDays', key: 'durationDays', sorter: true, },
    {
      title: 'Beneficios',
      dataIndex: 'benefits',
      key: 'benefits',
      render: (benefits) => {
        if (!benefits || typeof benefits !== 'object') return '-';

        return (
          <div>
            {Object.entries(benefits).map(
              ([key, value]) => (
                <div key={key} style={{ marginBottom: 4 }}>
                  <strong>{value.label || key}</strong>
                  <div style={{ fontSize: 12 }}>
                    Límite:{' '}
                    {value.limit !== undefined ? value.limit : 'Ilimitado'}
                    {' | '}
                    Permitido:{' '}
                    {value.can ? 'Sí' : 'No'}
                  </div>
                </div>
              )
            )}
          </div>
        );
      },
    },
    {
      title: 'Estado', dataIndex: 'isActive', key: 'isActive', sorter: true,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'volcano'}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
        <Title level={3} style={{ marginBottom: 0 }}>
          Membresías
        </Title>
        {can('memberships.add') && (<Tooltip title="Agregar Membresía">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="btn-add"
            aria-label="Agregar Membresía"
            onClick={() => navigate('/memberships/add')}
          />
        </Tooltip>)}
      </div>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.Search
            placeholder="Membresía"
            allowClear
            onSearch={onSearch}
            style={{ maxWidth: 360 }}
          />
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total) => `Total: ${total} regiones`
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default PlansList;
